import { useAuth } from '@clerk/clerk-react';
import { NextStepProvider, NextStepReact } from 'nextstepjs';
import { useMemo, useCallback } from 'react';

import { SidebarLayout } from '@/components/layout/sidebar-layout';
import { Providers } from '@/components/providers';
import { CloakOverlayToggle } from '@/components/CloakOverlayToggle';
import { UserDeviceTracker } from '@/components/UserDeviceTracker';
import { PreviewBanner } from '@/components/layout/preview-banner';
import { useAcquisitionTracking } from '@/hooks/use-acquisition-tracking';
import { useAnalyticsIdentity } from '@/hooks/use-analytics-identity';
import { useGaIdentity } from '@/hooks/use-ga-identity';
import { useAppBranding } from '@/hooks/use-app-branding';
import { useSyncI18nBranding } from '@/hooks/use-sync-i18n-branding';
import { config } from '@/lib/config';
import { AccountManagerProvider } from '@/contexts/AccountManagerContext';
import { GleapProvider } from '@/contexts/GleapProvider';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { useBrandHead } from '@/hooks/use-brand-head';
import { useReactRouterAdapter } from '@/lib/tours/react-router-adapter';
import { useOnboardingTour } from '@/hooks/use-onboarding-tour';
import { funnelEditorTour } from '@/lib/tours/funnel-editor-tour';
import { funnelOfferReminderTour } from '@/lib/tours/funnel-offer-reminder-tour';

// Import the page components
import OrdersPage from '@/app/orders/page';
import PaymentDetailsPage from '@/app/payments/[id]/page';
import PaymentsPage from '@/app/payments/page';
import DisputesPage from '@/app/disputes/page';
import DisputeDetailsPage from '@/app/disputes/[id]/page';
import ExternalRequestsPage from '@/app/external-requests/page';
import ExternalRequestDetailsPage from '@/app/external-requests/[id]/page';
import ProfilePage from '@/app/profile/page';
import SSOCallback from '@/app/sso-callback';
import InstanceConfigEditorPage from '@/app/stores/[id]/editor/[instanceId]/page';
import StoreLayout from '@/app/stores/[id]/layout';
import StorePluginsPage from '@/app/stores/[id]/plugins/page';
import StoreDetailsPage from '@/app/stores/[id]/products/page';
import StorefrontEditorPage from '@/app/stores/[id]/storefront/[funnelId]';
import DirectLinksPage from '@/app/stores/[id]/storefront/direct-links/page';
import StorefrontListPage from '@/app/stores/[id]/storefront/index';
import StorefrontMarketplacePage from '@/app/stores/[id]/storefront/marketplace';
import StorefrontMountPointsPage from '@/app/stores/[id]/storefront/mount-points';
import StoresPage from '@/app/stores/page';
import SubscriptionDetailsPageContainer from '@/app/subscriptions/[id]/page';
import SubscriptionsPage from '@/app/subscriptions/page';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import AccountManagerPage from './app/account-manager/page';
import ManageLayout from './app/manage/layout';
import ManageApplePayPage from './app/manage/apple-pay/page';
import ManageApplePayByokPage from './app/manage/apple-pay-byok/page';
import ManageApplePayDomainsPage from './app/manage/apple-pay-domains/page';
import ManageCryptoPaymentsPage from './app/manage/crypto-payments/page';
import AnalyticsPage from './app/analytics/page';
import ReportsLibraryPage from './app/reports/page';
import { ReportDetailPageContainer } from './app/reports/ReportDetailPageContainer';
import BillingPage from './app/billing/page';
import CompleteProfilePage from './app/complete-profile/page';
import CreateAccountPage from './app/create-account/page';
import CustomerDetailsPage from './app/customers/[id]/page';
import CustomersPage from './app/customers/page';
import { DomainsPageContainer } from './app/domains/page';
import PublicMarketplacePage from './app/marketplace/page';
import NoOrganizationPage from './app/no-organization/page';
import OrderDetailsPage from './app/orders/[id]/page';
import PlaygroundPage from './app/playground/page';
import ReferralPage from './app/referral/page';
import { AirwallexEditPage } from './app/processors/[id]/airwallex/edit/page';
import { CheckoutEditPage } from './app/processors/[id]/checkout/edit/page';
import { FinixEditPageContainer } from './app/processors/[id]/finix/edit/page';
import { MastercardEditPageContainer } from './app/processors/[id]/mastercard/edit/page';
import { NMIEditPageContainer } from './app/processors/[id]/nmi/edit/page';
import { RouteSenseEditPageContainer } from './app/processors/[id]/routesense/edit/page';
import { ProcessorDetailPageContainer } from './app/processors/[id]/page';
import { SandboxEditPage } from './app/processors/[id]/sandbox/edit/page';
import { StripeEditPageContainer } from './app/processors/[id]/stripe/edit/page';
import { TagadaPayEditPage } from './app/processors/[id]/tagadapay/edit/page';
import { TagadaPayRouterEditPage } from './app/processors/[id]/tagadapay-router/edit/page';
import { EfiPaymentsEditPage } from './app/processors/[id]/efipayments/edit/page';
import { MerchantScaleEditPage } from './app/processors/[id]/merchantscale/edit/page';
import { BakolaPayEditPage } from './app/processors/[id]/bakolapay/edit/page';
import { TrustFlowEditPageContainer } from './app/processors/[id]/trustflow/edit/page';
import { KessPayEditPageContainer } from './app/processors/[id]/kesspay/edit/page';
import { OvriEditPageContainer } from './app/processors/[id]/ovri/edit/page';
import { CCAvenueEditPageContainer } from './app/processors/[id]/ccavenue/edit/page';
import { NgeniusEditPageContainer } from './app/processors/[id]/ngenius/edit/page';
import { TapEditPageContainer } from './app/processors/[id]/tap/edit/page';
import { AuthorizeNetEditPageContainer } from './app/processors/[id]/authorizenet/edit/page';
import { ConvesioEditPageContainer } from './app/processors/[id]/convesio/edit/page';
import { AdyenEditPage } from './app/processors/[id]/adyen/edit/page';
import { PaylandEditPage } from './app/processors/[id]/payland/edit/page';
import { PaysafeEditPage } from './app/processors/[id]/paysafe/edit/page';
import { BlueSnapEditPage } from './app/processors/[id]/bluesnap/edit/page';
import { WorldpayEditPage } from './app/processors/[id]/worldpay/edit/page';
import { BraintreeEditPage } from './app/processors/[id]/braintree/edit/page';
import { MollieEditPage } from './app/processors/[id]/mollie/edit/page';
import { FiservEditPage } from './app/processors/[id]/fiserv/edit/page';
import { PaystackEditPage } from './app/processors/[id]/paystack/edit/page';
import { OnyxEditPage } from './app/processors/[id]/onyx/edit/page';
import { PayUEditPage } from './app/processors/[id]/payu/edit/page';
import { RazorpayEditPage } from './app/processors/[id]/razorpay/edit/page';
import { PayOneEditPage } from './app/processors/[id]/payone/edit/page';
import { GlobalPayEditPage } from './app/processors/[id]/globalpay/edit/page';
import { BuziPayEditPage } from './app/processors/[id]/buzipay/edit/page';
import { NetValveEditPage } from './app/processors/[id]/netvalve/edit/page';
import { XoalaEditPage } from './app/processors/[id]/xoala/edit/page';
import { UnlimitEditPage } from './app/processors/[id]/unlimit/edit/page';
import CreateProcessorPage from './app/processors/create/page';
import BlockRulesPage from './app/processors/block-rules/page';
import CreateBlockRulePage from './app/processors/block-rules/create/page';
import BlockRuleDetailsPage from './app/processors/block-rules/[id]/page';
// Org-level Payment Flows
import { PaymentFlowsListPage as OrgPaymentFlowsListPage } from './app/payment-flows/page';
import { CreatePaymentFlowPage as OrgCreatePaymentFlowPage } from './app/payment-flows/create/page';
import { PaymentFlowDetailsPage as OrgPaymentFlowDetailsPage } from './app/payment-flows/[flowId]/page';
import { EditPaymentFlowPage as OrgEditPaymentFlowPage } from './app/payment-flows/[flowId]/edit/page';
import { AbuseDetectionPage as OrgAbuseDetectionPage } from './app/payment-flows/[flowId]/abuse-detection/page';
import OrgSmartRetryPage from './app/payment-flows/[flowId]/smart-retry/page';
import { Rebill3DSPage as OrgRebill3DSPage } from './app/payment-flows/[flowId]/rebill-3ds/page';
import { isAuditTrailEnabled } from './lib/setupFeatures';
import AccessTokensPage from './app/settings/access-tokens/page';
import AuditTrailPage from './app/settings/audit-trail/page';
import DemoPage from './app/settings/demo/page';
import SettingsLayout from './app/settings/layout';
import OrganizationPage from './app/settings/organization/page';
import ApplePaySettingsPage from './app/settings/apple-pay/page';
import SetupPage from './app/setup/page';

import AdminLayout from './app/admin/layout';
import AdminRevenuePage from './app/admin/revenue/page';
import AdminProcessingPage from './app/admin/processing/page';
import AdminCryptoPage from './app/admin/crypto/page';
import AdminInvoicedPage from './app/admin/invoiced/page';
import AdminPartnersPage from './app/admin/partners/page';
import AdminAddPartnerPage from './app/admin/partners/new/page';
import AdminEditPartnerPage from './app/admin/partners/[slug]/edit/page';
import AdminIamPage from './app/admin/iam/page';
import AdminCrmContractTemplatePage from './app/admin/crm-contract-template/page';
import PartnerAdminLayout from './app/admin/partner/[partner]/layout';
import PartnerAdminRevenuePage from './app/admin/partner/[partner]/revenue/page';
import PartnerAdminProcessingPage from './app/admin/partner/[partner]/processing/page';
import PartnerAdminCryptoPage from './app/admin/partner/[partner]/crypto/page';
import PartnerAdminInvoicedPage from './app/admin/partner/[partner]/invoiced/page';
import PartnerAdminReportsPage from './app/admin/partner/[partner]/reports/page';
import PartnerStatementDetailPage from './app/admin/partner/[partner]/reports/[reportId]/page';
import PartnerAdminBillingPage from './app/admin/partner/[partner]/billing/page';
import PartnerAdminMerchantsPage from './app/admin/partner/[partner]/merchants/page';
import PartnerAdminTeamPage from './app/admin/partner/[partner]/team/page';
import PartnerAdminCrmKeysPage from './app/admin/partner/[partner]/crm-keys/page';
import PartnerAdminEmailDomainPage from './app/admin/partner/[partner]/email/page';
import PartnerAdminPayoutsPage from './app/admin/partner/[partner]/payouts/page';
import RxLayout from './app/rx/layout';
import RxSupportPage from './app/rx/page';
import PartnerRxSupportPage from './app/rx/[partner]/page';
import RestrictedPage from './app/restricted/page';
import ActivateAccountPage from './app/activate-account/page';
import SignInPage from './app/sign-in';
import SignUpPage from './app/sign-up';
import SignUpNewPage from './app/sign-up-new/page';
import AcceptInvitationPage from './app/accept-invitation';
import AcceptSignInPage from './app/accept-sign-in';
import { ClubSettingsPage } from './app/stores/[id]/settings/credits/page';
import { CustomerPortalSettingsPage } from './app/stores/[id]/settings/customer-portal/page';
import { RecurringDiscountsSettingsPage } from './app/stores/[id]/settings/recurring-discounts/page';
import IntegrationConfigPage from './app/stores/[id]/integrations/[provider]/page';
import IntegrationsListPage from './app/stores/[id]/integrations/page';
import CustomPaymentHubPage from './app/stores/[id]/integrations/custom-payment/page';
// Tagada Rx pages — conditionally surfaced when the `tagada_rx`
// integration is active for the store (gated at the page level).
import RxOverviewPage from './app/stores/[id]/rx/page';
import RxCasesPage from './app/stores/[id]/rx/cases/page';
import RxCaseDetailPage from './app/stores/[id]/rx/cases/[caseId]/page';
import RxClinicalConfigPage from './app/stores/[id]/rx/clinical-config/page';
import RxAffiliateTermsPage from './app/stores/[id]/rx/affiliate-terms/page';
import RxStatementPage from './app/stores/[id]/rx/statement/page';
import RxProductOfferingsPage from './app/stores/[id]/rx/product-offerings/page';
import RxStorefrontPage from './app/stores/[id]/rx/storefront/page';
// Store-level payment flows (for selecting default flow only)
import { PaymentFlowsListPage as StorePaymentFlowsListPage } from './app/stores/[id]/payment-flows/flows/page';
import { PostPurchaseRulePage } from './app/stores/[id]/post-purchase/[id]/page';
import CreatePostPurchaseRulePage from './app/stores/[id]/post-purchase/create/page';
import { PostPurchasePage } from './app/stores/[id]/post-purchase/page';
import EditProductPage from './app/stores/[id]/products/[productId]/edit/page';
import ProductDetailPageContainer from './app/stores/[id]/products/[productId]/page';
import VariantDetailsPageContainer from './app/stores/[id]/products/[productId]/variants/[id]/page';
import { CreateVariantPage } from './app/stores/[id]/products/[productId]/variants/create/page';
import CreateProductPage from './app/stores/[id]/products/create/page';
import { PromotionDetailsPageContainer } from './app/stores/[id]/promotions/discounts/[id]/page';
import { CreatePromotionPageContainer } from './app/stores/[id]/promotions/discounts/create/page';
import { DiscountsPage } from './app/stores/[id]/promotions/discounts/page';
import { PromotionAnalyticsPage } from './app/stores/[id]/promotions/analytics/page';
import { EditOfferPageContainer } from './app/stores/[id]/promotions/offers/[offerId]/page';
import CreateOfferPage from './app/stores/[id]/promotions/offers/create/page';
import { OffersPage } from './app/stores/[id]/promotions/offers/page';
import StoreEditPageContainer from './app/stores/[id]/settings/edit/page';
import { EditEmailTemplatePage } from './app/stores/[id]/settings/emails/[id]/page';
import CreateEmailTemplatePage from './app/stores/[id]/settings/emails/create/page';
import { EmailTemplatesPage } from './app/stores/[id]/settings/emails/page';
import EmailDomainSettingsPage from './app/stores/[id]/settings/emails/settings/page';
import { TranslationDetailPage } from './app/stores/[id]/settings/languages/[id]/page';
import { LanguagesPage } from './app/stores/[id]/settings/languages/page';
import { EditShippingRatePage } from './app/stores/[id]/settings/shippings/[id]/page';
import CreateShippingRatePage from './app/stores/[id]/settings/shippings/create/page';
import ShippingRatesPage from './app/stores/[id]/settings/shippings/page';
import TaxExemptionsPage from './app/stores/[id]/settings/taxes/exemptions/page';
import CreateTaxExemptionsPage from './app/stores/[id]/settings/taxes/exemptions/create/page';
import TaxRatesPage from './app/stores/[id]/settings/taxes/page';
import { EditUpsellPage } from './app/stores/[id]/upsells/[upsellId]/page';
import CreateUpsellPage from './app/stores/[id]/upsells/create/page';
import { UpsellsPage } from './app/stores/[id]/upsells/page';
import CreateStorePage from './app/stores/create/page';
import { AccessRestrictionGuard } from './components/guards/access-restriction-guard';
import { PaymentMethodValidGuard } from './components/guards/payment-method-valid-guard';
import { GuidedSetupGuard } from './components/guards/guided-setup-guard';
import { OrgRedirect } from './components/layout/org-redirect';
import { OrgRouteWrapper } from './components/layout/org-route-wrapper';
import { AdminAppLayout } from './components/layout/admin-app-layout';
import NotFound from './components/not-found';
import { ActivateAccountRedirect } from './components/redirects/activate-account-redirect';
import { SetupPaymentRedirect } from './components/redirects/setup-payment-redirect';
import { WhopReturnRedirect } from './components/redirects/whop-return-redirect';
import { ADMIN_ROUTES, MANAGE_ROUTES, ORG_ROUTES, PARTNER_ADMIN_ROUTES, PUBLIC_ROUTES, RX_ROUTES, STORE_ROUTES } from './config/router';
import { useAccountManager } from './hooks/use-account-manager';
import { useCrmV3Redirect } from './hooks/use-crm-v3-redirect';

// Redirect components that extract orgSlug from URL params
function RedirectToPaymentFlows() {
  const { orgSlug } = useParams();
  return <Navigate to={ORG_ROUTES(orgSlug || '').PAYMENT_FLOWS()} replace />;
}

function RedirectToPaymentFlowsWithProcessorsTab() {
  const { orgSlug } = useParams();
  return <Navigate to={`${ORG_ROUTES(orgSlug || '').PAYMENT_FLOWS()}?tab=processors`} replace />;
}

// Protected route wrapper component
function ProtectedRoute() {
  const { isSignedIn, isLoaded } = useAuth();
  const { isAccountManager, isLoading } = useAccountManager();
  const { orgSlug } = useParams();
  const leavingForV3 = useCrmV3Redirect(isLoaded && isSignedIn ? orgSlug ?? '' : '');

  // Wait for Clerk to load before making decisions
  if (!isLoaded) {
    return null; // OrgRouteWrapper already shows loading spinner
  }

  // If not signed in, redirect to local sign-in page
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // Wait for user info to load
  if (isLoading || leavingForV3) {
    return null; // OrgRouteWrapper already shows loading spinner
  }

  // Render the layout with child routes
  return (
    <SidebarLayout isAccountManager={isAccountManager}>
      {config.isPreview && <PreviewBanner />}
      <Outlet />
    </SidebarLayout>
  );
}

// Auth-only wrapper for admin / partner-admin sections.
// Unlike ProtectedRoute, this does NOT require an active Clerk org —
// admin pages are gated by global role (superadmin / partneradmin)
// and present cross-org data.
function AdminProtectedRoute() {
  const { isSignedIn, isLoaded } = useAuth();

  if (!isLoaded) return null;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;

  return (
    <AdminAppLayout>
      {config.isPreview && <PreviewBanner />}
      <Outlet />
    </AdminAppLayout>
  );
}

/**
 * Redirects legacy `/stores/:id/settings/emails/<rest>` URLs to the new
 * top-level `/stores/:id/emails/<rest>` location. Emails used to live under
 * Settings but were promoted to a first-class per-store section alongside
 * Funnels and Payment Flows.
 */
function LegacyEmailsRedirect() {
  const params = useParams();
  const orgSlug = params.orgSlug || '';
  const id = params.id || '';
  // react-router's splat param lives on `*`
  const rest = (params as Record<string, string | undefined>)['*'] || '';
  const target = `${STORE_ROUTES(orgSlug, id).EMAILS()}${rest ? `/${rest}` : ''}`;
  return <Navigate to={target} replace />;
}

/** `/partner-admin/:partner/rx` moved to `/rx/:partner` when the desk left the admin shell. */
function LegacyPartnerRxRedirect() {
  const { partner = '' } = useParams<{ partner: string }>();
  return <Navigate to={RX_ROUTES.PARTNER(partner)} replace />;
}

function App() {
  const { isLoaded } = useAuth();
  const orgRoutes = ORG_ROUTES(':orgSlug');
  const storeRoutes = STORE_ROUTES(':orgSlug', ':id');

  const brand = useAppBranding();
  useBrandHead();
  useSyncI18nBranding();
  useBrandTheme(brand);
  // First-touch acquisition capture + Clerk user metadata write. App-wide so
  // every org-creation path (create-account, no-organization, wizard, SSO)
  // sees the acquisition on the user before the org exists.
  useAcquisitionTracking();
  useAnalyticsIdentity();
  useGaIdentity();

  // Don't render anything while Clerk initializes
  // OrgRouteWrapper will show the loading spinner for org-scoped routes
  if (!isLoaded) {
    return null;
  }

  return (
    <Providers>
      <GleapProvider>
        <AccountManagerProvider>
          <NextStepProvider>
            <AppWithTours />
            <UserDeviceTracker />
            <CloakOverlayToggle />
          </NextStepProvider>
        </AccountManagerProvider>
      </GleapProvider>
    </Providers>
  );
}

// Separate component to use hooks inside NextStepProvider
function AppWithTours() {
  const orgRoutes = ORG_ROUTES(':orgSlug');
  const storeRoutes = STORE_ROUTES(':orgSlug', ':id');
  const { tours, onComplete, onSkip, onStepChange } = useOnboardingTour();

  // Combine onboarding tours with funnel editor tour + offer reminder
  const allTours = useMemo(() => [...tours, funnelEditorTour, funnelOfferReminderTour], [tours]);

  // Handle step change for both onboarding and funnel editor tours
  const handleStepChange = useCallback(
    (step: number, tourName: string | null) => {
      // Call onboarding tour handler
      onStepChange(step, tourName);

      // Dispatch event for funnel-side tours (editor setup + offer reminder)
      if (tourName === 'funnel-editor-setup' || tourName === 'funnel-offer-reminder') {
        window.dispatchEvent(
          new CustomEvent('funnel-tour-step-change', {
            detail: { step, tourName },
          }),
        );
      }
    },
    [onStepChange],
  );

  return (
    <NextStepReact
      navigationAdapter={useReactRouterAdapter}
      steps={allTours}
      onComplete={onComplete}
      onSkip={onSkip}
      onStepChange={handleStepChange}
      shadowRgb="59,130,246" // blue-500
      shadowOpacity="0.5"
      clickThroughOverlay={true}
      scrollToTop={false}
    >
      <Routes>
        {/* Public routes */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/sign-up-new" element={<SignUpNewPage />} />
        {/* Partner-staff Clerk invitation landing — see app/accept-invitation.tsx */}
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        {/* Backward-compat: legacy invitation links pointed at the
            Next.js primary's /dashboard/sign-up. The host now serves
            the SPA, so route those links to the same handler. */}
        <Route path="/dashboard/sign-up" element={<AcceptInvitationPage />} />
        {/* Apply-flow handoff landing — see app/accept-sign-in.tsx.
            Consumes the sign-in token minted by tagadapay.io's apply
            BFF (clerk.signInTokens) and bounces to the satellite
            dashboard with an active session. Distinct from the
            invitation flow above (signUp.create vs signIn.create). */}
        <Route path="/accept-sign-in" element={<AcceptSignInPage />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route path={PUBLIC_ROUTES.NO_ORGANIZATION} element={<NoOrganizationPage />} />
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="/create-account" element={<CreateAccountPage />} />
        <Route path="/marketplace" element={<PublicMarketplacePage />} />

        {/* Legacy setup-payment route - redirect to first org */}
        <Route path="/setup-payment" element={<SetupPaymentRedirect />} />

        {/* Whop Platforms KYC callback landing — resolves org slug, forwards to org-scoped integrations */}
        <Route path="/integrations/whop-return" element={<WhopReturnRedirect />} />

        {/* Redirect root to first org */}
        <Route path={'/'} element={<OrgRedirect />} />
        <Route path={'/org'} element={<OrgRedirect />} />

        {/* Organization-scoped routes */}
        <Route element={<OrgRouteWrapper />}>
          {/* Activation funnel — full-screen, no CRM sidebar */}
          <Route path={orgRoutes.ACTIVATE_ACCOUNT()} element={<ActivateAccountPage />} />

          <Route path={orgRoutes.BASE()} element={<ProtectedRoute />}>
            <Route path="setup-payment" element={<ActivateAccountRedirect />} />
            <Route path="restricted" element={<RestrictedPage />} />

            {/* Access Restriction Guard - blocks unpaid/suspended accounts */}
            <Route element={<AccessRestrictionGuard />}>
              {/* Hard gate — no CRM access without a valid billing card */}
              <Route element={<PaymentMethodValidGuard />}>
                {/* Guided setup — full-screen wizard; guard redirects store-less merchants here */}
                <Route element={<GuidedSetupGuard />}>
                  <Route path="setup" element={<SetupPage />} />
                  <Route index element={<AnalyticsPage />} />
                  <Route path={orgRoutes.REPORTS()} element={<ReportsLibraryPage />} />
                  <Route
                    path={orgRoutes.REPORT_DETAILS(':reportId')}
                    element={<ReportDetailPageContainer />}
                  />
                  <Route path={orgRoutes.ORDERS()} element={<OrdersPage />} />
                  <Route path={orgRoutes.ORDER_DETAILS(':id')} element={<OrderDetailsPage />} />
                  <Route path={orgRoutes.SUBSCRIPTIONS()} element={<SubscriptionsPage />} />
                  <Route
                    path={orgRoutes.SUBSCRIPTION_DETAILS(':id')}
                    element={<SubscriptionDetailsPageContainer />}
                  />
                  <Route path={orgRoutes.PAYMENTS()} element={<PaymentsPage />} />
                  <Route path={orgRoutes.PAYMENT_DETAILS(':id')} element={<PaymentDetailsPage />} />
                  <Route path={orgRoutes.DISPUTES()} element={<DisputesPage />} />
                  <Route path={orgRoutes.DISPUTE_DETAILS(':id')} element={<DisputeDetailsPage />} />
                  <Route path={orgRoutes.EXTERNAL_REQUESTS()} element={<ExternalRequestsPage />} />
                  <Route
                    path={orgRoutes.EXTERNAL_REQUEST_DETAILS(':id')}
                    element={<ExternalRequestDetailsPage />}
                  />
                  <Route path={orgRoutes.STORES()} element={<StoresPage />} />
                  <Route path={orgRoutes.STORE_CREATE()} element={<CreateStorePage />} />
                  <Route path={orgRoutes.STORE(':id')} element={<StoreLayout />}>
                    <Route index element={<Navigate to={'general'} replace />} />
                    <Route path={storeRoutes.STORE_GENERAL()}>
                      <Route index element={<Navigate to={'products'} replace />} />
                      <Route path={storeRoutes.PRODUCTS()}>
                        <Route index element={<StoreDetailsPage />} />
                        <Route path={storeRoutes.PRODUCT_CREATE()} element={<CreateProductPage />} />
                        <Route path={storeRoutes.PRODUCT_DETAILS(':productId')}>
                          <Route index element={<ProductDetailPageContainer />} />
                          <Route
                            path={storeRoutes.VARIANT_CREATE(':productId')}
                            element={<CreateVariantPage />}
                          />
                          <Route
                            path={storeRoutes.VARIANT_DETAILS(':productId', ':variantId')}
                            element={<VariantDetailsPageContainer />}
                          />
                          <Route
                            path={storeRoutes.PRODUCT_EDIT(':productId')}
                            element={<EditProductPage />}
                          />
                        </Route>
                      </Route>
                      <Route path={storeRoutes.PROMOTIONS()}>
                        <Route index element={<Navigate to={'discounts'} replace />} />
                        <Route path={storeRoutes.OFFERS()}>
                          <Route index element={<OffersPage />} />
                          <Route path={storeRoutes.OFFER_CREATE()} element={<CreateOfferPage />} />
                          <Route
                            path={storeRoutes.OFFER_EDIT(':offerId')}
                            element={<EditOfferPageContainer />}
                          />
                        </Route>
                        <Route
                          path={storeRoutes.PROMOTIONS_ANALYTICS()}
                          element={<PromotionAnalyticsPage />}
                        />
                        <Route path={storeRoutes.DISCOUNTS()}>
                          <Route index element={<DiscountsPage />} />
                          <Route
                            path={storeRoutes.DISCOUNT_CREATE()}
                            element={<CreatePromotionPageContainer />}
                          />
                          <Route
                            path={storeRoutes.DISCOUNT_DETAILS(':promotionId')}
                            element={<PromotionDetailsPageContainer />}
                          />
                        </Route>
                      </Route>
                      <Route path={storeRoutes.POST_PURCHASE()}>
                        <Route index element={<PostPurchasePage />} />
                        <Route
                          path={storeRoutes.POST_PURCHASE_CREATE()}
                          element={<CreatePostPurchaseRulePage />}
                        />
                        <Route
                          path={storeRoutes.POST_PURCHASE_EDIT(':postPurchaseId')}
                          element={<PostPurchaseRulePage />}
                        />
                      </Route>
                      <Route path={storeRoutes.UPSELLS()}>
                        <Route index element={<UpsellsPage />} />
                        <Route path={storeRoutes.UPSELL_CREATE()} element={<CreateUpsellPage />} />
                        <Route path={storeRoutes.UPSELL_EDIT(':upsellId')} element={<EditUpsellPage />} />
                      </Route>

                      <Route path={storeRoutes.TAXES()}>
                        <Route index element={<TaxRatesPage />} />
                        <Route path={storeRoutes.TAX_EXEMPTIONS()} element={<TaxExemptionsPage />} />
                        <Route
                          path={storeRoutes.TAX_EXEMPTIONS_CREATE()}
                          element={<CreateTaxExemptionsPage />}
                        />
                      </Route>
                      <Route path={storeRoutes.SHIPPINGS()}>
                        <Route index element={<ShippingRatesPage />} />
                        <Route path={storeRoutes.SHIPPING_CREATE()} element={<CreateShippingRatePage />} />
                        <Route
                          path={storeRoutes.SHIPPING_EDIT(':shippingId')}
                          element={<EditShippingRatePage />}
                        />
                      </Route>
                    </Route>
                    {/* <Route index element={<StoreEditPageContainer />} /> */}
                    <Route path={storeRoutes.STORE_EDIT()} element={<StoreEditPageContainer />} />
                    <Route
                      path={storeRoutes.CUSTOMER_PORTAL_SETTINGS()}
                      element={<CustomerPortalSettingsPage />}
                    />
                    <Route
                      path={storeRoutes.RECURRING_DISCOUNTS_SETTINGS()}
                      element={<RecurringDiscountsSettingsPage />}
                    />
                    <Route path={storeRoutes.CLUB_SETTINGS()} element={<ClubSettingsPage />} />
                    <Route path={storeRoutes.CLUB_SETTINGS_TAB(':tab')} element={<ClubSettingsPage />} />
                    {/* Back-compat: old credits URL → club settings */}
                    <Route
                      path={`${storeRoutes.STORE_BASE()}/settings/credits`}
                      element={<Navigate to={storeRoutes.CLUB_SETTINGS_TAB('membership')} replace />}
                    />
                    <Route path={storeRoutes.TRANSLATIONS()}>
                      <Route index element={<LanguagesPage />} />
                      <Route
                        path={storeRoutes.TRANSLATION_EDIT(':transId')}
                        element={<TranslationDetailPage />}
                      />
                    </Route>
                    <Route path={storeRoutes.EMAILS()}>
                      <Route index element={<EmailTemplatesPage />}></Route>
                      <Route
                        path={storeRoutes.EMAILS_SETTINGS()}
                        element={<EmailDomainSettingsPage />}
                      ></Route>
                      <Route
                        path={storeRoutes.EMAIL_TEMPLATE_EDIT(':emailTemplateId')}
                        element={<EditEmailTemplatePage />}
                      ></Route>
                      <Route path={storeRoutes.EMAILS_CREATE()} element={<CreateEmailTemplatePage />}></Route>
                    </Route>
                    {/* Backwards-compat: emails used to live under /settings/emails.
                    Redirect any old bookmarks to the new top-level section. */}
                    <Route
                      path={`${storeRoutes.STORE_BASE()}/settings/emails`}
                      element={<Navigate to={storeRoutes.EMAILS()} replace />}
                    />
                    <Route
                      path={`${storeRoutes.STORE_BASE()}/settings/emails/*`}
                      element={<LegacyEmailsRedirect />}
                    />
                    {/* Store-level payment flows (for selecting default flow only) */}
                    <Route path={storeRoutes.PAYMENT_FLOWS()}>
                      <Route index element={<Navigate to="flows" replace />} />
                      <Route path="flows" element={<StorePaymentFlowsListPage />} />
                    </Route>
                    <Route path={storeRoutes.STOREFRONT()}>
                      <Route index element={<Navigate to={'funnels'} replace />} />
                      <Route
                        path={storeRoutes.EDITOR(':instanceId')}
                        element={<InstanceConfigEditorPage />}
                      />
                      <Route path={storeRoutes.FUNNELS()} element={<StorefrontListPage />} />
                      <Route path={storeRoutes.STOREFRONT_DIRECT_LINKS()} element={<DirectLinksPage />} />
                      <Route
                        path={storeRoutes.STOREFRONT_MARKETPLACE()}
                        element={<StorefrontMarketplacePage />}
                      />
                      <Route
                        path={storeRoutes.STOREFRONT_MOUNT_POINTS()}
                        element={<StorefrontMountPointsPage />}
                      />
                      <Route path={storeRoutes.PLUGINS()} element={<StorePluginsPage />} />
                      <Route
                        path={storeRoutes.STOREFRONT_EDITOR(':funnelId')}
                        element={<StorefrontEditorPage />}
                      />
                    </Route>
                    <Route path={storeRoutes.INTEGRATIONS()} element={<IntegrationsListPage />} />
                    <Route
                      path={storeRoutes.INTEGRATION_CUSTOM_PAYMENT_HUB()}
                      element={<CustomPaymentHubPage />}
                    />
                    <Route
                      path={storeRoutes.INTEGRATION_CONFIG(':provider')}
                      element={<IntegrationConfigPage />}
                    />

                    {/* Tagada Rx — paid add-on. Pages self-gate on
                        `rx.integrationStatus.state === 'active'`; the
                        routes themselves are always reachable so the
                        activation flow has a stable URL. */}
                    <Route path={storeRoutes.RX_OVERVIEW()} element={<RxOverviewPage />} />
                    <Route path={storeRoutes.RX_CASES()} element={<RxCasesPage />} />
                    <Route path={storeRoutes.RX_CASE_DETAIL(':caseId')} element={<RxCaseDetailPage />} />
                    <Route path={storeRoutes.RX_CLINICAL_CONFIG()} element={<RxClinicalConfigPage />} />
                    <Route path={storeRoutes.RX_AFFILIATE_TERMS()} element={<RxAffiliateTermsPage />} />
                    <Route path={storeRoutes.RX_STATEMENT()} element={<RxStatementPage />} />
                    <Route path={storeRoutes.RX_PRODUCT_OFFERINGS()} element={<RxProductOfferingsPage />} />
                    <Route path={storeRoutes.RX_STOREFRONT()} element={<RxStorefrontPage />} />

                    <Route path={storeRoutes.PLAYGROUND()} element={<PlaygroundPage />} />
                  </Route>
                  <Route path={orgRoutes.DOMAINS()} element={<DomainsPageContainer />} />
                  <Route
                    path={orgRoutes.PROCESSORS()}
                    element={<RedirectToPaymentFlowsWithProcessorsTab />}
                  />
                  <Route path={orgRoutes.BLOCK_RULES()} element={<BlockRulesPage />} />
                  <Route path={orgRoutes.BLOCK_RULE_CREATE()} element={<CreateBlockRulePage />} />
                  <Route path={orgRoutes.BLOCK_RULE_DETAILS(':id')} element={<BlockRuleDetailsPage />} />
                  <Route path={orgRoutes.PROCESSOR_CREATE()} element={<CreateProcessorPage />} />
                  <Route
                    path={orgRoutes.PROCESSOR_DETAILS(':id')}
                    element={<ProcessorDetailPageContainer />}
                  />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_STRIPE(':id')}
                    element={<StripeEditPageContainer />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_NMI(':id')} element={<NMIEditPageContainer />} />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_ROUTESENSE(':id')}
                    element={<RouteSenseEditPageContainer />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_FINIX(':id')} element={<FinixEditPageContainer />} />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_MASTERCARD(':id')}
                    element={<MastercardEditPageContainer />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_AIRWALLEX(':id')} element={<AirwallexEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_CHECKOUT(':id')} element={<CheckoutEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_TAGADAPAY(':id')} element={<TagadaPayEditPage />} />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_TAGADAPAY_ROUTER(':id')}
                    element={<TagadaPayRouterEditPage />}
                  />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_EFIPAYMENTS(':id')}
                    element={<EfiPaymentsEditPage />}
                  />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_MERCHANTSCALE(':id')}
                    element={<MerchantScaleEditPage />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_BAKOLAPAY(':id')} element={<BakolaPayEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_SANDBOX(':id')} element={<SandboxEditPage />} />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_TRUSTFLOW(':id')}
                    element={<TrustFlowEditPageContainer />}
                  />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_KESSPAY(':id')}
                    element={<KessPayEditPageContainer />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_OVRI(':id')} element={<OvriEditPageContainer />} />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_CCAVENUE(':id')}
                    element={<CCAvenueEditPageContainer />}
                  />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_NGENIUS(':id')}
                    element={<NgeniusEditPageContainer />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_TAP(':id')} element={<TapEditPageContainer />} />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_AUTHORIZENET(':id')}
                    element={<AuthorizeNetEditPageContainer />}
                  />
                  <Route
                    path={orgRoutes.PROCESSOR_EDIT_CONVESIO(':id')}
                    element={<ConvesioEditPageContainer />}
                  />
                  <Route path={orgRoutes.PROCESSOR_EDIT_ADYEN(':id')} element={<AdyenEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_PAYLAND(':id')} element={<PaylandEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_PAYSAFE(':id')} element={<PaysafeEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_BLUESNAP(':id')} element={<BlueSnapEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_WORLDPAY(':id')} element={<WorldpayEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_BRAINTREE(':id')} element={<BraintreeEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_MOLLIE(':id')} element={<MollieEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_FISERV(':id')} element={<FiservEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_PAYSTACK(':id')} element={<PaystackEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_ONYX(':id')} element={<OnyxEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_PAYU(':id')} element={<PayUEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_RAZORPAY(':id')} element={<RazorpayEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_PAYONE(':id')} element={<PayOneEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_GLOBALPAY(':id')} element={<GlobalPayEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_BUZIPAY(':id')} element={<BuziPayEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_NETVALVE(':id')} element={<NetValveEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_XOALA(':id')} element={<XoalaEditPage />} />
                  <Route path={orgRoutes.PROCESSOR_EDIT_UNLIMIT(':id')} element={<UnlimitEditPage />} />
                  {/* Org-level Payment Flows (global, not store-specific) */}
                  <Route path={orgRoutes.PAYMENT_FLOWS()} element={<OrgPaymentFlowsListPage />} />
                  <Route path={orgRoutes.PAYMENT_FLOW_CREATE()} element={<OrgCreatePaymentFlowPage />} />
                  <Route
                    path={orgRoutes.PAYMENT_FLOW_DETAILS(':flowId')}
                    element={<OrgPaymentFlowDetailsPage />}
                  />
                  <Route path={orgRoutes.PAYMENT_FLOW_EDIT(':flowId')} element={<OrgEditPaymentFlowPage />} />
                  <Route
                    path={orgRoutes.PAYMENT_FLOW_ABUSE_DETECTION(':flowId')}
                    element={<OrgAbuseDetectionPage />}
                  />
                  <Route
                    path={orgRoutes.PAYMENT_FLOW_SMART_RETRY(':flowId')}
                    element={<OrgSmartRetryPage />}
                  />
                  <Route path={orgRoutes.PAYMENT_FLOW_REBILL_3DS(':flowId')} element={<OrgRebill3DSPage />} />
                  <Route path={orgRoutes.CUSTOMERS()} element={<CustomersPage />} />
                  <Route path={orgRoutes.CUSTOMER_DETAILS(':id')} element={<CustomerDetailsPage />} />
                  <Route path={orgRoutes.PROFILE()} element={<ProfilePage />} />
                  <Route path={orgRoutes.REFERRAL()} element={<ReferralPage />} />
                  <Route path={orgRoutes.BILLING()} element={<BillingPage />} />
                  <Route path={orgRoutes.ACCOUNT_MANAGER()} element={<AccountManagerPage />} />
                  {/* Staff-only platform operations. The page gates on the
                      account-manager / superadmin role itself. */}
                  {/* Moved to the org-independent /manage/apple-pay. */}
                  <Route
                    path={orgRoutes.MANAGE_APPLE_PAY()}
                    element={<Navigate to={MANAGE_ROUTES.APPLE_PAY} replace />}
                  />
                  <Route
                    path={orgRoutes.SETTINGS()}
                    element={
                      <SettingsLayout>
                        <Outlet />
                      </SettingsLayout>
                    }
                  >
                    <Route index element={<Navigate to={'organization'} replace />} />
                    <Route path={orgRoutes.SETTINGS_ORGANIZATION()} element={<OrganizationPage />} />
                    <Route path={orgRoutes.SETTINGS_DEMO()} element={<DemoPage />} />
                    <Route path={orgRoutes.SETTINGS_ACCESS_TOKENS()} element={<AccessTokensPage />} />
                    <Route path={orgRoutes.SETTINGS_APPLE_PAY()} element={<ApplePaySettingsPage />} />
                    {isAuditTrailEnabled() && (
                      <Route path={orgRoutes.SETTINGS_AUDIT_TRAIL()} element={<AuditTrailPage />} />
                    )}
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Catch-all route for protected routes (404) */}
          <Route path="*" element={<NotFound type="protected" />} />
        </Route>

        {/* Staff operations. Not org-scoped — nothing here belongs to an org.
            The page enforces the account-manager / superadmin role itself, the
            same way the admin pages below do. */}
        <Route element={<AdminProtectedRoute />}>
          <Route path={MANAGE_ROUTES.BASE} element={<ManageLayout />}>
            <Route path={MANAGE_ROUTES.APPLE_PAY} element={<ManageApplePayPage />} />
            <Route path={MANAGE_ROUTES.APPLE_PAY_BYOK} element={<ManageApplePayByokPage />} />
            <Route path={MANAGE_ROUTES.APPLE_PAY_DOMAINS} element={<ManageApplePayDomainsPage />} />
            <Route path={MANAGE_ROUTES.CRYPTO_PAYMENTS} element={<ManageCryptoPaymentsPage />} />
          </Route>
        </Route>

        {/* TagadaRx desk (rx_ops / rx_admin, superadmin). Not admin, not
            org-scoped — RxLayout gates platform vs partner scope. */}
        <Route element={<AdminProtectedRoute />}>
          <Route path={RX_ROUTES.BASE} element={<RxLayout />}>
            <Route index element={<RxSupportPage />} />
            <Route path=":partner" element={<PartnerRxSupportPage />} />
          </Route>
          {/* The desk used to be a tab of the admin shells. */}
          <Route path="/admin/rx" element={<Navigate to={RX_ROUTES.BASE} replace />} />
          <Route path="/partner-admin/:partner/rx" element={<LegacyPartnerRxRedirect />} />
        </Route>

        {/* Admin backoffice (superadmin only). Not org-scoped. */}
        <Route element={<AdminProtectedRoute />}>
          <Route
            path={ADMIN_ROUTES.BASE}
            element={
              <AdminLayout>
                <Outlet />
              </AdminLayout>
            }
          >
            <Route index element={<Navigate to={'revenue'} replace />} />
            <Route path={ADMIN_ROUTES.REVENUE} element={<AdminRevenuePage />} />
            <Route path={ADMIN_ROUTES.PROCESSING} element={<AdminProcessingPage />} />
            <Route path={ADMIN_ROUTES.CRYPTO} element={<AdminCryptoPage />} />
            <Route path={ADMIN_ROUTES.INVOICED} element={<AdminInvoicedPage />} />
            <Route path={ADMIN_ROUTES.PARTNERS} element={<AdminPartnersPage />} />
            <Route path={ADMIN_ROUTES.PARTNERS_NEW} element={<AdminAddPartnerPage />} />
            <Route path="/admin/partners/:slug/edit" element={<AdminEditPartnerPage />} />
            <Route path={ADMIN_ROUTES.IAM} element={<AdminIamPage />} />
            <Route path={ADMIN_ROUTES.CRM_CONTRACT_TEMPLATE} element={<AdminCrmContractTemplatePage />} />
          </Route>

          {/* Partner admin (partneradmin role or superadmin). URL uses :partner param. */}
          <Route
            path={PARTNER_ADMIN_ROUTES(':partner').BASE}
            element={
              <PartnerAdminLayout>
                <Outlet />
              </PartnerAdminLayout>
            }
          >
            <Route index element={<Navigate to={'revenue'} replace />} />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').REVENUE} element={<PartnerAdminRevenuePage />} />
            <Route
              path={PARTNER_ADMIN_ROUTES(':partner').PROCESSING}
              element={<PartnerAdminProcessingPage />}
            />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').CRYPTO} element={<PartnerAdminCryptoPage />} />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').INVOICED} element={<PartnerAdminInvoicedPage />} />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').REPORTS} element={<PartnerAdminReportsPage />} />
            <Route
              path={PARTNER_ADMIN_ROUTES(':partner').REPORT_DETAILS(':reportId')}
              element={<PartnerStatementDetailPage />}
            />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').BILLING} element={<PartnerAdminBillingPage />} />
            <Route
              path={PARTNER_ADMIN_ROUTES(':partner').MERCHANTS}
              element={<PartnerAdminMerchantsPage />}
            />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').TEAM} element={<PartnerAdminTeamPage />} />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').CRM_KEYS} element={<PartnerAdminCrmKeysPage />} />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').EMAIL} element={<PartnerAdminEmailDomainPage />} />
            <Route path={PARTNER_ADMIN_ROUTES(':partner').PAYOUTS} element={<PartnerAdminPayoutsPage />} />
          </Route>
        </Route>
        {/* Catch-all route for public routes (404) - must be after protected routes */}
        <Route path="*" element={<NotFound type="public" />} />
      </Routes>
    </NextStepReact>
  );
}

export default App;
